import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useGetLegalEntitiesQuery } from "src/common/service/nexus/nexusApi.js";
import { useUser } from "src/common/context/userContextUtils.js";
import {
	getGravatarProfileUrl,
	getGravatarUrl,
} from "src/features/planning/utils/getGravatarUrl.js";
import { useAuth0Profile } from "src/common/utils/hooks/useAuth0Profile";
import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import { readLastActiveContext } from "src/common/utils/lastActiveContext.js";
import { ExperienceList } from "src/features/experiences/ExperienceList.js";
import { useTranslation } from "react-i18next";
import {
	ALL_COMPANY_DOMAINS,
	ALL_LEGAL_ENTITIES,
} from "src/common/utils/lastActiveContext.js";
import { useLoadedCompanyDomains } from "src/routes/routeDataHooks.js";
import { ProfilePreferences } from "./ProfilePreferences.js";
import { ProfileView } from "./ProfileView.js";

export const ProfilePage = ({ onClose }: { onClose?: () => void } = {}) => {
	const { t } = useTranslation();
	const { user } = useUser();
	const auth0Profile = useAuth0Profile();
	const companyDomains = useLoadedCompanyDomains();
	const { clear: clearLastActiveContext } = useLastActiveContext();
	const [lastActiveContext, setLastActiveContext] = useState(() =>
		readLastActiveContext(),
	);

	const roleNames = Array.from(
		new Set(user.accessGroups.map((accessGroup) => accessGroup.name)),
	);
	// WHY only for a super admin: their companyDomains is the whole fleet, while everyone else already receives just their own.
	const explicitCompanyIds = Array.from(
		new Set(user.accessGroups.map((accessGroup) => accessGroup.companyId)),
	);
	const explicitDomains = user.superAdmin
		? companyDomains.filter((companyDomain) =>
				explicitCompanyIds.includes(companyDomain.id),
			)
		: companyDomains;
	// WHY the remembered domain, not a loaded one: the modal opens outside a domain route too.
	const rememberedDomainId = lastActiveContext?.companyDomainId;
	const rememberedAllDomains = rememberedDomainId === ALL_COMPANY_DOMAINS;
	// WHY: "all" is the picker's sentinel for no domain, not an id the API knows.
	const companyDomainId = rememberedAllDomains ? undefined : rememberedDomainId;
	// WHY currentData: after a reset the skipped query would otherwise keep rendering the previous domain's companies.
	const { currentData: companies } = useGetLegalEntitiesQuery(
		companyDomainId ?? skipToken,
	);
	const defaultDomainName = rememberedAllDomains
		? t("All domains")
		: companyDomains.find(
				(companyDomain) => `${companyDomain.id}` === rememberedDomainId,
			)?.name;
	// WHY the sentinel first: domain scope remembers "all", which matches no company.
	const defaultCompanyName =
		lastActiveContext?.legalEntityId === ALL_LEGAL_ENTITIES
			? t("All companies")
			: companies?.find(
					(company) => `${company.id}` === lastActiveContext?.legalEntityId,
				)?.name;

	return (
		<div className="space-y-4">
			<ProfileView
				avatarSrc={getGravatarUrl(user.email)}
				gravatarProfileUrl={getGravatarProfileUrl(user.email)}
				name={auth0Profile.name ?? user.name}
				email={user.email}
				superAdmin={user.superAdmin}
				roleNames={roleNames}
				domains={explicitDomains}
				companies={companies ?? []}
				companyDomainId={companyDomainId}
				defaultDomainName={defaultDomainName}
				defaultCompanyName={defaultCompanyName}
				onResetDefaultCompany={() => {
					clearLastActiveContext();
					setLastActiveContext(undefined);
				}}
			/>
			<ProfilePreferences />
			<ExperienceList onShowAgain={() => onClose?.()} />
		</div>
	);
};
